import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import {
  Calendar, ChevronLeft, ChevronRight, FileText, Film, Image as ImageIcon,
  Loader2, CheckCircle, X, Download, ExternalLink, Eye, Tag, Clock, LayoutList,
} from 'lucide-react'
import { Badge } from '@/components/common/Badge'
import { Card } from '@/components/common/Card'
import { StatCard } from '@/components/common/StatCard'
import { apiClient, pedApi } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

// ── Tipi ─────────────────────────────────────────────────────────────────────
interface GuestContent {
  id: string
  title: string
  type: string
  status: string
  platforms: string[]
  caption?: string
  body?: string
  hashtags?: string
  scheduledAt?: string
  submittedAt?: string
  updatedAt: string
  creator?: { id: string; firstName: string; lastName: string }
  client?: { id: string; name: string }
  editorialPlan?: { id: string; title: string; project?: { id: string; name: string } }
  socialFormat?: {
    id: string; name: string; type: string; description?: string | null
    platforms: string[]; guidelines?: string | null
  } | null
  assets: { id: string; filename: string; mimeType: string; size: number; type: string; url: string }[]
}

const PLATFORM_LABEL: Record<string, string> = {
  INSTAGRAM: 'Instagram', FACEBOOK: 'Facebook', TIKTOK: 'TikTok',
  LINKEDIN: 'LinkedIn', YOUTUBE: 'YouTube', TWITTER: 'Twitter/X', PINTEREST: 'Pinterest',
}

const STATUS_LABEL: Record<string, string> = {
  IN_REVIEW: 'Da approvare', CHANGES_REQUESTED: 'Modifiche richieste',
  APPROVED: 'Da approvare', CLIENT_APPROVED: 'Approvato',
  SCHEDULED: 'Programmato', PUBLISHED: 'Pubblicato',
}

const STATUS_VARIANT: Record<string, 'warning' | 'info' | 'success' | 'destructive' | 'neutral'> = {
  IN_REVIEW: 'warning', CHANGES_REQUESTED: 'destructive',
  APPROVED: 'warning', CLIENT_APPROVED: 'success',
  SCHEDULED: 'info', PUBLISHED: 'success',
}

const TYPE_ICON: Record<string, typeof FileText> = {
  VIDEO: Film, REEL: Film, STORY: ImageIcon, POST: FileText,
  CAROUSEL: ImageIcon, ARTICLE: FileText, INFOGRAPHIC: ImageIcon,
}

// Stati su cui il guest può agire (approvare / richiedere modifiche)
const ACTIONABLE = new Set(['IN_REVIEW', 'APPROVED'])

function fmtDate(iso?: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' })
}
function fmtTime(iso?: string) {
  if (!iso) return ''
  return new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })
}

// ── Pagina ───────────────────────────────────────────────────────────────────
export function GuestPedPage() {
  const queryClient = useQueryClient()
  const user = useAuthStore((s) => s.user)
  const canAct = !!user?.isGuest && user?.guestPedPermission !== 'view'

  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [previewAsset, setPreviewAsset] = useState<{ url: string; filename: string; mimeType: string } | null>(null)

  const today = new Date()
  const [currentYear, setCurrentYear] = useState(today.getFullYear())
  const [currentMonth, setCurrentMonth] = useState(today.getMonth())

  const { data: contents, isLoading } = useQuery<GuestContent[]>({
    queryKey: ['ped', 'guest-plan'],
    queryFn: async () => {
      const res = await pedApi.getGuestPlan()
      return (res as any)?.data ?? res ?? []
    },
    refetchInterval: 30_000,
  })

  const approveMutation = useMutation({
    mutationFn: async (contentId: string) => { await apiClient.post(`/ped/contents/${contentId}/approve`, {}) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['ped', 'guest-plan'] }) },
  })
  const rejectMutation = useMutation({
    mutationFn: async ({ contentId, notes }: { contentId: string; notes: string }) => {
      await apiClient.patch(`/ped/contents/${contentId}/request-changes`, { comment: notes })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ped', 'guest-plan'] })
      queryClient.invalidateQueries({ queryKey: ['ped', 'content-comments'] })
    },
  })

  // Progetti disponibili (derivati dai contenuti)
  const projects = useMemo(() => {
    const m = new Map<string, string>()
    for (const c of contents ?? []) {
      const p = c.editorialPlan?.project
      if (p?.id) m.set(p.id, p.name)
    }
    return Array.from(m.entries()).map(([id, name]) => ({ id, name }))
  }, [contents])

  const filtered = useMemo(() => {
    const list = contents ?? []
    return selectedProjectId ? list.filter((c) => c.editorialPlan?.project?.id === selectedProjectId) : list
  }, [contents, selectedProjectId])

  // Contenuto attualmente aperto (derivato dai dati freschi)
  const selected = useMemo(() => (contents ?? []).find((c) => c.id === selectedId) ?? null, [contents, selectedId])

  // Calendar helpers
  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()
  const firstDayOfWeek = (new Date(currentYear, currentMonth, 1).getDay() + 6) % 7
  const monthLabel = new Date(currentYear, currentMonth).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
  const byDay: Record<number, GuestContent[]> = {}
  for (const c of filtered) {
    if (!c.scheduledAt) continue
    const d = new Date(c.scheduledAt)
    if (d.getFullYear() === currentYear && d.getMonth() === currentMonth) {
      const day = d.getDate()
      ;(byDay[day] ??= []).push(c)
    }
  }
  const undated = filtered.filter((c) => !c.scheduledAt)
  const prevMonth = () => { if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear((y) => y - 1) } else setCurrentMonth((m) => m - 1) }
  const nextMonth = () => { if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear((y) => y + 1) } else setCurrentMonth((m) => m + 1) }

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const da = a.scheduledAt ? new Date(a.scheduledAt).getTime() : Number.MAX_SAFE_INTEGER
    const db = b.scheduledAt ? new Date(b.scheduledAt).getTime() : Number.MAX_SAFE_INTEGER
    return da - db
  }), [filtered])

  const pendingCount = filtered.filter((c) => ACTIONABLE.has(c.status)).length
  const approvedCount = filtered.filter((c) => c.status === 'CLIENT_APPROVED' || c.status === 'PUBLISHED').length
  const totalCount = filtered.length

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Piano Editoriale</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {pendingCount > 0 ? `${pendingCount} contenut${pendingCount === 1 ? 'o' : 'i'} da approvare` : 'Tutti i contenuti sono aggiornati'}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* View toggle */}
          <div className="flex items-center bg-slate-100 rounded-full p-1">
            <button
              onClick={() => setViewMode('calendar')}
              className={`flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-full transition-colors ${viewMode === 'calendar' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <Calendar className="w-3.5 h-3.5" /> Calendario
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`flex items-center gap-1.5 px-3.5 py-1 text-xs font-semibold rounded-full transition-colors ${viewMode === 'list' ? 'bg-white text-primary-600 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <LayoutList className="w-3.5 h-3.5" /> Lista
            </button>
          </div>
          {viewMode === 'calendar' && (
            <div className="flex items-center gap-1.5">
              <button onClick={prevMonth} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm font-semibold text-slate-700 capitalize min-w-[130px] text-center tabular-nums">{monthLabel}</span>
              <button onClick={nextMonth} className="w-8 h-8 flex items-center justify-center rounded-full text-slate-500 bg-white border border-slate-200 hover:bg-slate-50 hover:text-slate-700 transition-colors"><ChevronRight className="w-4 h-4" /></button>
            </div>
          )}
        </div>
      </div>

      {/* Riepilogo */}
      {totalCount > 0 && (
        <div className="grid grid-cols-3 gap-3 sm:gap-4">
          <StatCard label="Da approvare" value={pendingCount} icon={Clock} tone="warning" hint={pendingCount === 1 ? 'contenuto' : 'contenuti'} />
          <StatCard label="Approvati" value={approvedCount} icon={CheckCircle} tone="success" hint={approvedCount === 1 ? 'contenuto' : 'contenuti'} />
          <StatCard label="Totale" value={totalCount} icon={FileText} tone="primary" hint={totalCount === 1 ? 'contenuto' : 'contenuti'} />
        </div>
      )}

      {/* Project filter */}
      {projects.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSelectedProjectId('')}
            className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${selectedProjectId === '' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
          >
            Tutti
          </button>
          {projects.map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedProjectId(p.id)}
              className={`px-3 py-1.5 text-xs font-medium rounded-full border transition-colors ${selectedProjectId === p.id ? 'bg-primary-600 text-white border-primary-600' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'}`}
            >
              {p.name}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {filtered.length === 0 && (
        <Card padding="lg" className="border-slate-200">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <CheckCircle className="w-12 h-12 text-success-400 mb-4" />
            <p className="text-base font-medium text-slate-700">Nessun contenuto nel piano</p>
            <p className="text-sm text-slate-400 mt-1">Quando ci saranno contenuti pronti li vedrai qui.</p>
          </div>
        </Card>
      )}

      {/* CALENDAR */}
      {viewMode === 'calendar' && filtered.length > 0 && (
        <>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-card overflow-hidden">
            <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((d) => (
                <div key={d} className="py-2.5 text-center text-[11px] font-semibold text-slate-400 uppercase tracking-[0.12em]">{d}</div>
              ))}
            </div>
            <div className="grid grid-cols-7">
              {Array.from({ length: firstDayOfWeek }).map((_, i) => (
                <div key={`e-${i}`} className="min-h-[92px] border-b border-r border-slate-200 bg-slate-50/50" />
              ))}
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1
                const items = byDay[day] ?? []
                const isToday = day === today.getDate() && currentMonth === today.getMonth() && currentYear === today.getFullYear()
                return (
                  <div key={day} className={`min-h-[92px] border-b border-r border-slate-200 p-1.5 ${isToday ? 'bg-primary-50' : ''}`}>
                    <span className={`text-xs font-semibold tabular-nums ${isToday ? 'text-white bg-primary-600 rounded-full w-6 h-6 inline-flex items-center justify-center' : 'text-slate-600'}`}>{day}</span>
                    <div className="mt-1 space-y-0.5">
                      {items.map((c) => {
                        const Icon = TYPE_ICON[c.type] ?? FileText
                        const dot = c.status === 'CLIENT_APPROVED' || c.status === 'PUBLISHED' ? 'bg-success-500'
                          : c.status === 'CHANGES_REQUESTED' ? 'bg-danger-500'
                          : ACTIONABLE.has(c.status) ? 'bg-warning-500' : 'bg-primary-400'
                        return (
                          <button
                            key={c.id}
                            onClick={() => setSelectedId(c.id)}
                            className="w-full text-left px-1.5 py-1 rounded-md text-xs truncate hover:bg-slate-100 transition-colors flex items-center gap-1"
                            title={c.title}
                          >
                            <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${dot}`} />
                            <Icon className="w-3 h-3 flex-shrink-0 text-slate-400" />
                            <span className="truncate font-medium text-slate-700">{c.title}</span>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
          {undated.length > 0 && (
            <div>
              <h3 className="text-sm font-medium text-slate-600 mb-2">Senza data programmata</h3>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {undated.map((c) => <ContentRow key={c.id} c={c} onClick={() => setSelectedId(c.id)} />)}
              </div>
            </div>
          )}
        </>
      )}

      {/* LIST */}
      {viewMode === 'list' && filtered.length > 0 && (
        <div className="space-y-2">
          {sorted.map((c) => <ContentRow key={c.id} c={c} onClick={() => setSelectedId(c.id)} showDate />)}
        </div>
      )}

      {/* Detail modal */}
      {selected && createPortal(
        <GuestContentModal
          content={selected}
          canAct={canAct}
          onClose={() => setSelectedId(null)}
          onPreview={setPreviewAsset}
          approving={approveMutation.isPending}
          rejecting={rejectMutation.isPending}
          onApprove={() => approveMutation.mutate(selected.id)}
          onReject={(notes) => rejectMutation.mutate({ contentId: selected.id, notes })}
        />,
        document.body,
      )}

      {/* Asset preview */}
      {previewAsset && createPortal(
        <AssetPreview asset={previewAsset} onClose={() => setPreviewAsset(null)} />,
        document.body,
      )}
    </div>
  )
}

// ── Riga contenuto (lista / senza data) ───────────────────────────────────────
function ContentRow({ c, onClick, showDate }: { c: GuestContent; onClick: () => void; showDate?: boolean }) {
  const Icon = TYPE_ICON[c.type] ?? FileText
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white rounded-2xl border border-slate-200 shadow-card px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3"
    >
      <Icon className="w-4 h-4 text-slate-400 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-slate-800 truncate">{c.title}</p>
        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
          {c.editorialPlan?.project && <span className="text-[10px] text-slate-400">{c.editorialPlan.project.name}</span>}
          {c.socialFormat && <span className="inline-flex items-center gap-1 text-[10px] text-primary-600"><Tag className="w-2.5 h-2.5" />{c.socialFormat.name}</span>}
          {c.platforms.map((p) => <span key={p} className="text-[10px] text-slate-400">{PLATFORM_LABEL[p] ?? p}</span>)}
        </div>
      </div>
      {showDate && c.scheduledAt && (
        <div className="hidden sm:flex items-center gap-1 text-xs text-slate-400 whitespace-nowrap tabular-nums">
          <Calendar className="w-3 h-3" />{fmtDate(c.scheduledAt)}
        </div>
      )}
      <Badge variant={STATUS_VARIANT[c.status] ?? 'neutral'} size="sm">{STATUS_LABEL[c.status] ?? c.status}</Badge>
    </button>
  )
}

// ── Modale dettaglio contenuto ────────────────────────────────────────────────
function GuestContentModal({
  content, canAct, onClose, onPreview, onApprove, onReject, approving, rejecting,
}: {
  content: GuestContent
  canAct: boolean
  onClose: () => void
  onPreview: (a: { url: string; filename: string; mimeType: string }) => void
  onApprove: () => void
  onReject: (notes: string) => void
  approving: boolean
  rejecting: boolean
}) {
  const [rejectMode, setRejectMode] = useState(false)
  const [rejectText, setRejectText] = useState('')
  const text = content.caption || content.body || ''
  const isActionable = ACTIONABLE.has(content.status)

  // Commenti del contenuto → mostra le richieste di modifica inviate (testo, autore, data)
  const { data: comments } = useQuery<Array<{ id: string; type: string; text: string; createdAt: string; authorName?: string; user?: { firstName: string; lastName: string } | null }>>({
    queryKey: ['ped', 'content-comments', content.id],
    queryFn: async () => {
      const res = await pedApi.getContentComments(content.id)
      return (res as any)?.data ?? res ?? []
    },
  })
  // Richieste di modifica, deduplicate per testo (tollera eventuali doppioni storici)
  const changeRequests = (() => {
    const seen = new Set<string>()
    return (comments ?? []).filter((c) => {
      if (c.type !== 'change_request') return false
      const key = c.text.trim()
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
  })()

  const openAsset = async (asset: GuestContent['assets'][number]) => {
    try {
      const res = await pedApi.streamAssetUrl(asset.id)
      const blob = res instanceof Blob ? res : (res as any)?.data instanceof Blob ? (res as any).data : null
      if (blob) { onPreview({ url: URL.createObjectURL(blob), filename: asset.filename, mimeType: asset.mimeType }); return }
    } catch { /* fallback sotto */ }
    try {
      const res2 = await pedApi.getAssetUrl(asset.id)
      const presigned = (res2 as any)?.data?.url ?? (res2 as any)?.data?.data?.url
      if (presigned) onPreview({ url: presigned, filename: asset.filename, mimeType: asset.mimeType })
    } catch { /* silenzioso */ }
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-modal w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-slate-200">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-base font-semibold text-slate-900">{content.title}</h2>
              <Badge variant={STATUS_VARIANT[content.status] ?? 'neutral'} size="sm">{STATUS_LABEL[content.status] ?? content.status}</Badge>
            </div>
            <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
              {content.editorialPlan?.project && <span>{content.editorialPlan.project.name}</span>}
              {content.scheduledAt && <span className="inline-flex items-center gap-1 tabular-nums"><Calendar className="w-3 h-3" />{fmtDate(content.scheduledAt)} {fmtTime(content.scheduledAt)}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors flex-shrink-0"><X className="w-4 h-4" /></button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {changeRequests.length > 0 && (
            <div className="rounded-xl bg-danger-50 border border-slate-200 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-danger-600">
                <Clock className="w-3.5 h-3.5" />
                {content.status === 'CHANGES_REQUESTED'
                  ? 'Modifiche richieste — il team sta rilavorando il contenuto'
                  : 'Modifiche richieste in precedenza'}
              </div>
              {changeRequests.map((cr) => (
                <div key={cr.id} className="bg-white rounded-lg p-2.5 border border-slate-200">
                  <p className="text-sm text-danger-700 whitespace-pre-wrap">{cr.text}</p>
                  <p className="text-[10px] text-slate-400 mt-1 tabular-nums">
                    {cr.user ? `${cr.user.firstName} ${cr.user.lastName}` : cr.authorName ?? ''} · {fmtDate(cr.createdAt)} {fmtTime(cr.createdAt)}
                  </p>
                </div>
              ))}
            </div>
          )}

          {/* Piattaforme + format */}
          <div className="flex items-center gap-2 flex-wrap">
            {content.platforms.map((p) => (
              <span key={p} className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-slate-100 text-slate-600">{PLATFORM_LABEL[p] ?? p}</span>
            ))}
            {content.socialFormat && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-primary-50 text-primary-600">
                <Tag className="w-3 h-3" />{content.socialFormat.name}
              </span>
            )}
          </div>

          {/* Testo / caption */}
          {text && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">{content.caption ? 'Caption' : 'Testo'}</p>
              <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-xl p-3 border border-slate-200">{text}</p>
            </div>
          )}
          {content.hashtags && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Hashtag</p>
              <p className="text-xs text-primary-600">{content.hashtags}</p>
            </div>
          )}

          {/* Linee guida format */}
          {content.socialFormat?.guidelines && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-1">Linee guida</p>
              <p className="text-xs text-slate-500 whitespace-pre-wrap">{content.socialFormat.guidelines}</p>
            </div>
          )}

          {/* Allegati */}
          {content.assets.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-slate-600 mb-2">File allegati ({content.assets.length})</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {content.assets.map((a) => {
                  const isImg = a.mimeType.startsWith('image/')
                  const isVid = a.mimeType.startsWith('video/')
                  return (
                    <button key={a.id} onClick={() => openAsset(a)} className="flex items-center gap-2 px-3 py-2 bg-white rounded-lg border border-slate-200 hover:bg-slate-50 transition-colors text-left">
                      {isImg ? <ImageIcon className="w-4 h-4 text-primary-500" /> : isVid ? <Film className="w-4 h-4 text-purple-500" /> : <FileText className="w-4 h-4 text-slate-400" />}
                      <span className="text-xs text-slate-700 truncate flex-1">{a.filename}</span>
                      <Eye className="w-3.5 h-3.5 text-primary-600 flex-shrink-0" />
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>

        {/* Footer azioni */}
        {canAct && isActionable && (
          <div className="border-t border-slate-200 px-5 py-3">
            {rejectMode ? (
              <div className="space-y-2">
                <textarea
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-none"
                  rows={3}
                  placeholder="Descrivi le modifiche richieste..."
                  value={rejectText}
                  onChange={(e) => setRejectText(e.target.value)}
                  autoFocus
                />
                <div className="flex items-center gap-2">
                  <button
                    className="px-3 py-1.5 text-xs font-medium rounded-lg bg-danger-600 text-white hover:bg-danger-700 disabled:opacity-50 transition-colors"
                    disabled={!rejectText.trim() || rejecting}
                    onClick={() => onReject(rejectText.trim())}
                  >
                    {rejecting ? 'Invio...' : 'Invia richiesta modifiche'}
                  </button>
                  <button className="px-3 py-1.5 text-xs font-medium rounded-lg text-slate-600 hover:bg-slate-100 transition-colors" onClick={() => { setRejectMode(false); setRejectText('') }}>Annulla</button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-success-600 text-white hover:bg-success-700 disabled:opacity-50 flex items-center justify-center gap-1.5 transition-colors"
                  disabled={approving}
                  onClick={onApprove}
                >
                  <CheckCircle className="w-4 h-4" />{approving ? 'Approvazione...' : 'Approva'}
                </button>
                <button
                  className="flex-1 px-3 py-2 text-sm font-medium rounded-lg bg-danger-50 text-danger-600 hover:bg-danger-100 transition-colors"
                  onClick={() => setRejectMode(true)}
                >
                  Richiedi modifiche
                </button>
              </div>
            )}
          </div>
        )}
        {content.status === 'CLIENT_APPROVED' && (
          <div className="border-t border-slate-200 px-5 py-3 text-center text-sm font-medium text-success-700 bg-success-50 flex items-center justify-center gap-1.5">
            <CheckCircle className="w-4 h-4" /> Hai approvato questo contenuto
          </div>
        )}
      </div>
    </div>
  )
}

// ── Anteprima asset ───────────────────────────────────────────────────────────
function AssetPreview({ asset, onClose }: { asset: { url: string; filename: string; mimeType: string }; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div className="relative bg-white rounded-3xl shadow-modal w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <span className="text-sm font-medium text-slate-800 truncate">{asset.filename}</span>
          <div className="flex items-center gap-2 flex-shrink-0">
            <a href={asset.url} download={asset.filename} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100" onClick={(e) => e.stopPropagation()}><Download className="w-4 h-4" /></a>
            <a href={asset.url} target="_blank" rel="noopener noreferrer" className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100" onClick={(e) => e.stopPropagation()}><ExternalLink className="w-4 h-4" /></a>
            <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <div className="flex-1 overflow-auto flex items-center justify-center p-4 bg-slate-50 min-h-[300px]">
          {asset.mimeType.startsWith('image/') ? (
            <img src={asset.url} alt={asset.filename} className="max-w-full max-h-[75vh] object-contain rounded-lg shadow-sm" />
          ) : asset.mimeType.startsWith('video/') ? (
            <video src={asset.url} controls autoPlay className="max-w-full max-h-[75vh] rounded-lg shadow-sm" />
          ) : asset.mimeType === 'application/pdf' ? (
            <iframe src={asset.url} className="w-full h-[75vh] rounded-lg" title={asset.filename} />
          ) : (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">Anteprima non disponibile</p>
              <a href={asset.url} download={asset.filename} className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"><Download className="w-4 h-4" />Scarica file</a>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
